import React from "react";
import { motion } from "framer-motion";
import u1 from "../../accests/udawalawa/udawalawa (2).jpeg"
import u2 from "../../accests/udawalawa/u2.jpeg"
import u3 from "../../accests/yala/heroImg.jfif"
import u4 from "../../accests/galle/galle (4).jpeg"
import u5 from "../../accests/galle/mala.jpeg"
import u6 from "../../accests/galle/moon.jpg"
import yal from "../../accests/common/yal.jpg"

const day1Highlights = [
    {
        title: "Udawalawe National Park – Morning Safari",
        desc: "Begin your adventure with an early morning safari at Udawalawe National Park, renowned for its large elephant herds and rich biodiversity. The park is home to leopards, water buffalo, crocodiles, and over 180 bird species, offering exceptional wildlife viewing opportunities.",
        img: u1,
        wiki: "https://en.wikipedia.org/wiki/Udawalawe_National_Park",
    },
    {
        title: "Udawalawe Elephant Transit Home",
        desc: "Visit the Elephant Transit Home, a rehabilitation center dedicated to orphaned and injured elephants. Observe feeding time from a safe distance as young elephants are cared for before being released back into the wild.",
        img: u2,
        wiki: "https://en.wikipedia.org/wiki/Elephant_Transit_Home",
    },
    {
        title: "Yala National Park – Evening Safari",
        desc: "Proceed to Yala National Park, famous for having the highest density of leopards in the world. Enjoy an exciting safari through diverse landscapes, spotting elephants, sloth bears, spotted deer, crocodiles, and vibrant birdlife.",
        img: u3,
        wiki: "https://en.wikipedia.org/wiki/Yala_National_Park",
    },
];

const day2Highlights = [
    {
        title: "Galle Dutch Fort",
        desc: "Explore the historic Galle Dutch Fort, a UNESCO World Heritage Site featuring colonial architecture, cobblestone streets, charming cafés, and stunning ocean views along the southern coastline.",
        img: u4,
        wiki: "https://en.wikipedia.org/wiki/Galle_Fort",
    },
    {
        title: "Tsunami Museum",
        desc: "Visit the Tsunami Museum to gain insight into the 2004 Indian Ocean tsunami and its impact on coastal communities, offering a meaningful and educational experience.",
        img: u5,
        wiki: "https://en.wikipedia.org/wiki/2004_Indian_Ocean_earthquake_and_tsunami",
    },
    {
        title: "Meetiyagoda Moonstone Mine",
        desc: "Discover the unique moonstone mines of Meetiyagoda, where rare gemstones are extracted and transformed into exquisite jewelry using traditional techniques.",
        img: u6,
        wiki: "https://en.wikipedia.org/wiki/Moonstone_(gemstone)",
    },
];

const info = [
    { label: "Trip Duration", value: "02 Days" },
    { label: "Language", value: "English" },
    { label: "Best Time to Visit", value: "Year-round" },
    { label: "Transfers", value: "Hotel – Safari Parks – Hotel" },
];

const inclusions = [
    "Private air-conditioned transportation (Car, Van, Mini Coach, or Luxury Bus)",
    "Carefully selected hotels, villas, or resorts on a half-board basis",
    "Experienced and licensed English speaking chauffeur guide",
    "All entrance fees to national parks and safari jeep charges",
    "All sightseeing and excursion costs as outlined in the itinerary",
    "Unlimited complimentary bottled mineral water during the tour",
    "All applicable government taxes, service charges, and levies",
];

const exclusions = [
    "Meals and beverages not included in the stated meal plan",
    "International or domestic flight tickets and visa arrangements",
    "Personal expenses such as tips, laundry, and optional activities",
];

const travelTips = [
    "Age & Health Advisory: Not recommended for children under 6, expectant mothers, or guests with cardiac or respiratory conditions.",
    "Dress Comfortably: Light cotton clothing and closed footwear are ideal for safari excursions.",
    "Sun Protection: Bring sunscreen, a hat, and sunglasses due to tropical weather conditions.",
    "Wildlife Etiquette: Maintain silence during safaris to enhance wildlife sightings.",
    "Hydration: Stay hydrated throughout the journey; bottled water is provided.",
];

export default function UdawalaweYalaTwoDayTour() {
    return (
        <div className="overflow-x-hidden">

            {/* Hero Section */}
            <section className="relative h-[70vh] w-full overflow-hidden">
                <img
                    src={yal}
                    alt="Udawalawe and Yala Two Day Tour"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/40 flex flex-col items-center justify-center text-center px-4">
                    <h1 className="text-4xl md:text-5xl text-white font-bold mb-2">
                        Udawalawe & Yala Two Day Safari
                    </h1>
                    <p className="text-lg md:text-xl text-white/90">
                        A thrilling wildlife experience through Sri Lanka’s most iconic national parks
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 py-16 space-y-16">

            {/* Day 1 */}
            <section className="space-y-12">
                <h2 className="text-3xl font-bold text-center">Day 1 – Wildlife Encounters</h2>
                {day1Highlights.map((item, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className={`md:flex md:items-center md:space-x-8 bg-white shadow-lg rounded-xl overflow-hidden ${
                            idx % 2 === 1 ? "md:flex-row-reverse" : ""
                        }`}
                    >
                        <img src={item.img} alt={item.title} className="md:w-1/2 h-64 object-cover" />
                        <div className="p-6 md:w-1/2">
                            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                            <p className="text-gray-700">{item.desc}</p>
                            <a
                                href={item.wiki}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-4 text-blue-600 font-semibold hover:text-blue-800"
                            >
                                Read More →
                            </a>
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Day 2 */}
            <section className="space-y-12">
                <h2 className="text-3xl font-bold text-center">Day 2 – Southern Coast Discovery</h2>
                {day2Highlights.map((item, idx) => (
                    <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        className={`md:flex md:items-center md:space-x-8 bg-white shadow-lg rounded-xl overflow-hidden ${
                            idx % 2 === 1 ? "md:flex-row-reverse" : ""
                        }`}
                    >
                        <img src={item.img} alt={item.title} className="md:w-1/2 h-64 object-cover" />
                        <div className="p-6 md:w-1/2">
                            <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                            <p className="text-gray-700">{item.desc}</p>
                            <a
                                href={item.wiki}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block mt-4 text-blue-600 font-semibold hover:text-blue-800"
                            >
                                Read More →
                            </a>
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Info */}
            <section className="bg-green-50 p-8 rounded-xl shadow-lg grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {info.map((item, idx) => (
                    <div key={idx} className="text-center">
                        <h4 className="text-lg font-semibold">{item.label}</h4>
                        <p className="text-gray-700">{item.value}</p>
                    </div>
                ))}
            </section>

            {/* Inclusions & Exclusions */}
            <section className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-2xl font-bold mb-4">Tour Inclusions</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {inclusions.map((i, idx) => <li key={idx}>{i}</li>)}
                    </ul>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-2xl font-bold mb-4">Tour Exclusions</h3>
                    <ul className="list-disc list-inside space-y-2 text-gray-700">
                        {exclusions.map((e, idx) => <li key={idx}>{e}</li>)}
                    </ul>
                </div>
            </section>

            {/* Travel Tips */}
            <section className="bg-gray-50 p-6 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Travel Tips</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                    {travelTips.map((tip, idx) => <li key={idx}>{tip}</li>)}
                </ul>
            </section>

            {/* CTA – WhatsApp Only */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mt-14 text-center"
            >
                <motion.a
                    whileHover={{ scale: 1.08 }}
                    whileTap={{ scale: 0.95 }}
                    href="https://wa.me/94770163670?text=Hello%20I%20would%20like%20to%20book%20the%20Udawalawe%20%26%20Yala%20Two%20Day%20Safari%20Tour.%20Please%20share%20availability%20and%20pricing.%20Thank%20you."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center
        px-14 py-4 bg-green-600 text-white
        font-semibold text-lg rounded-full
        shadow-xl hover:bg-green-500
        transition-all duration-300"
                >
                    Book This Safari Adventure
                </motion.a>

                <p className="mt-4 text-sm text-gray-600">
                    💬 Instant WhatsApp Support • 🐘 Private Safaris • 🚙 Luxury Vehicles
                </p>
            </motion.div>

            </div>
        </div>
    );
}
